import { renderSize } from '../render/quality';
import type { QualitySettings } from '../render/types';

/* WHY a plain width/height pair, not DOMRect or ResizeObserverEntry: the DOM adapter can feed this
   from canvas.clientWidth/clientHeight or from an observer entry's content box -- either source
   reduces to these two numbers, which is all this decision needs. */
export interface CssSize {
  readonly width: number;
  readonly height: number;
}

export interface ResizePlan {
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly renderWidth: number;
  readonly renderHeight: number;
}

/* WHY strictly positive: a hidden container, an ancestor with display:none, or a canvas that has
   not had its first layout yet all measure 0 (or, for an observer entry in exotic cases, negative)
   in at least one axis. three.js accepts setSize(0, 0) without complaint, but the GL driver then
   rejects every draw into that target with GL_INVALID_FRAMEBUFFER_OPERATION (zero-size
   attachment), so a non-positive size is never valid to render at, only one to wait out. */
export function isRenderable(size: CssSize): boolean {
  return size.width > 0 && size.height > 0;
}

/* WHY null rather than a plan with a 0 or negative dimension: null forces every caller to decide
   explicitly what "not renderable yet" means for it (skip sizing, skip drawing) instead of one
   trusting a numeric plan at face value and sizing a render target to 0 by accident. */
export function planResize(
  size: CssSize,
  dpr: number,
  quality: QualitySettings
): ResizePlan | null {
  if (!isRenderable(size)) return null;

  const { width: renderWidth, height: renderHeight } = renderSize(
    size.width,
    size.height,
    dpr,
    quality
  );
  return { cssWidth: size.width, cssHeight: size.height, renderWidth, renderHeight };
}
